import { useEffect, useState } from "react";
import { Eye, Users, Heart, MessageCircle, Share2, TrendingUp, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError, formatCount } from "@/lib/api";

const METRICS = [["views", "Visualizações", Eye], ["unique", "Pessoas alcançadas", Users], ["likes", "Curtidas", Heart], ["comments", "Comentários", MessageCircle], ["shares", "Compartilhamentos", Share2]];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

function DailyBars({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.views));
  if (!daily.length) return <p className="py-6 text-center text-sm text-muted-foreground" data-testid="report-no-data">Ainda sem visualizações nesta campanha.</p>;
  return (
    <div className="space-y-1.5" data-testid="report-daily">
      {daily.map((d) => (
        <div key={d.day} className="flex items-center gap-3 text-xs">
          <span className="w-12 shrink-0 text-muted-foreground">{d.day.slice(8, 10)}/{d.day.slice(5, 7)}</span>
          <div className="h-5 flex-1 overflow-hidden rounded-md bg-white/5">
            <div className="h-full rounded-md bg-[hsl(var(--gold))] transition-[width] duration-700" style={{ width: `${(100 * d.views) / max}%` }} />
          </div>
          <span className="w-24 shrink-0 text-right font-semibold">{formatCount(d.views)} · <Heart size={10} className="inline" /> {d.likes}</span>
        </div>
      ))}
    </div>
  );
}

export function SponsorReportDialog({ shortId, onClose }) {
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/super/shorts/${shortId}/report`).then((x) => setR(x.data)).catch((e) => setErr(formatApiError(e))); }, [shortId]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto overflow-x-hidden" data-testid="sponsor-report-dialog">
        <DialogHeader>
          <DialogTitle>Relatório da campanha</DialogTitle>
          {r && <DialogDescription className="line-clamp-2 break-words">{r.caption} · {r.author_name}</DialogDescription>}
        </DialogHeader>
        {err && <p className="text-sm text-red-300" data-testid="report-error">{err}</p>}
        {!r && !err && <Loader2 className="mx-auto animate-spin text-muted-foreground" />}
        {r && (
          <div className="space-y-5">
            <p className="text-xs text-muted-foreground" data-testid="report-period">
              {r.active ? "Em curso" : "Encerrada"} · Início {fmtDate(r.started_at)} · {r.active ? `Fim ${r.until ? fmtDate(r.until) : "sem data"}` : `Fim ${fmtDate(r.ended_at || r.until)}`} · Prioridade {r.priority}
            </p>
            <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-3">
              {METRICS.map(([k, label, Icon]) => (
                <div key={k} className="rounded-2xl bg-white/5 p-3" data-testid={`report-total-${k}`}>
                  <Icon size={15} className="text-[hsl(var(--gold))]" />
                  <p className="mt-1 text-xl font-extrabold">{formatCount(r.totals[k])}</p>
                  <p className="text-[11px] text-muted-foreground">{label}</p>
                </div>
              ))}
              <div className="rounded-2xl bg-[hsl(var(--gold))]/10 p-3" data-testid="report-total-engagement">
                <TrendingUp size={15} className="text-[hsl(var(--gold))]" />
                <p className="mt-1 text-xl font-extrabold">{r.totals.engagement_rate}%</p>
                <p className="text-[11px] text-muted-foreground">Taxa de interação</p>
              </div>
            </div>
            <div><p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-[hsl(var(--gold))]">Visualizações por dia</p><DailyBars daily={r.daily} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
